import React, { useState } from "react"
import toTitleCase from "../Functions/toTitleCase"

// Shared so the Add and Edit pricing modals label the same car model
// identically -- an edit that re-picked a model from a differently-worded
// list would look like a different model to whoever reviews the price.
export const carModelOptionLabel = (carModel) => (
    toTitleCase(carModel.make || "") + " " + carModel.name
    + (carModel.series ? " | " + carModel.series : "")
    + (carModel.groupType ? " | " + carModel.groupType.name : " | No Group Selected")
)

export const carModelShortLabel = (carModel) => (
    `${toTitleCase(carModel.make || "")} ${toTitleCase(carModel.name)}`.trim()
)

// One model per line for a title attribute. The listing cell only has room for
// the first model and only shows that one's group, so when a template covers
// several this tooltip is the only place the full set -- and each model's own
// group -- is visible.
export const carModelTooltipLine = (carModel) => (
    carModelShortLabel(carModel)
    + (carModel.series ? ` (${carModel.series})` : "")
    + " — " + (carModel.groupType ? carModel.groupType.name : "No group")
)

export const carModelTooltip = (carModels) => carModels.map(carModelTooltipLine).join("\n")

// The listing shows one group line for a whole template, but a template can
// cover models from different groups. Naming only the first model's group
// reads as a fact about the template when it is not one.
export const carModelGroupNames = (carModels) => [
    ...new Set(carModels.map(carModel => (carModel.groupType ? carModel.groupType.name : "No group")))
]

export const carModelGroupSummary = (groupNames) => {
    if (groupNames.length === 0) return "--"
    // A lone "No group" stays "--", the table's usual empty marker. It only
    // earns words once there is something to contrast it with.
    if (groupNames.length === 1) return groupNames[0] === "No group" ? "--" : groupNames[0]
    if (groupNames.length === 2) return groupNames.join(", ")
    return `${groupNames[0]}, ${groupNames[1]} +${groupNames.length - 2}`
}

export const carModelOption = (carModel) => ({
    value: carModel.id,
    label: carModelOptionLabel(carModel),
    // Carried alongside the long option label so summaries can show a readable
    // name without the series and group-type text the picker needs to tell two
    // same-named models apart. react-select ignores keys it does not know.
    shortLabel: carModelShortLabel(carModel),
})

// Order-insensitive id-set equality. Used to tell whether a car-model
// multi-select's current selection actually differs from what the record
// started with, so a save that touched nothing else can omit carModels
// instead of the backend reading it as a scope change.
export const sameIds = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join()

// Every car-model multi-select shares this contract: only a keystroke changes
// the filter, so the search survives a pick instead of resetting the whole
// list. react-select also fires this with 'set-value' right after a pick
// (ignored, so the search stays) and with 'menu-close'/'input-blur' when the
// menu goes away, where clearing means reopening starts fresh instead of on a
// stale filter. `setSearch` may be a useState setter or a class setState
// wrapper -- anything that accepts the new search string.
export const makeCarModelSearchHandler = (setSearch) => (value, { action }) => {
    if (action === 'input-change') {
        setSearch(value)
    } else if (action === 'menu-close' || action === 'input-blur') {
        setSearch('')
    }
}

// The react-select options list for a car-model connection, shared so every
// picker (Add and Edit, pricing and promotion) derives the same option shape
// from the same AllCarModels query result.
export const carModelOptionsFromConnection = (connection) => (
    (connection?.edges || [])
        .filter(item => item.node.make && item.node.name)
        .map(item => carModelOption(item.node))
)

// The Edit modals' live summary panel names the first selected model and
// counts the rest, with the full comma-joined list available as a hover
// title -- shared so a template with no models reads the same "For All
// Models" fallback everywhere.
export const carModelSummaryLabel = (selectedCarModels) => (
    selectedCarModels.length > 0
        ? selectedCarModels[0].shortLabel + (selectedCarModels.length > 1 ? `, +${selectedCarModels.length - 1} more` : '')
        : 'For All Models'
)

export const carModelSummaryTitle = (selectedCarModels) => selectedCarModels.map(option => option.shortLabel).join(', ')

// Shared car-model selection state for the Edit modals: tracks the live pick
// against the ids the record started with, so a save can tell whether the
// selection actually changed. `initialCarModels` is the option list (already
// run through carModelOption) the record starts with.
export const useCarModelSelection = (initialCarModels) => {
    const [selectedCarModels, setSelectedCarModels] = useState(initialCarModels)
    const [carModelSearch, setCarModelSearch] = useState('')

    const initialCarModelIds = initialCarModels.map(option => option.value)
    const selectedCarModelIds = selectedCarModels.map(option => option.value)
    const carModelsChanged = !sameIds(initialCarModelIds, selectedCarModelIds)

    return {
        selectedCarModels,
        setSelectedCarModels,
        carModelSearch,
        handleCarModelSearchChange: makeCarModelSearchHandler(setCarModelSearch),
        selectedCarModelIds,
        carModelsChanged,
    }
}

export const StatusChip = ({ on, onText, offText }) => (
    <span className={"listing-chip " + (on ? "listing-chip--on" : "listing-chip--off")}>
        <i className={"fa " + (on ? "fa-check" : "fa-times")} aria-hidden="true"></i>
        {on ? onText : offText}
    </span>
)

export const VisibilityChip = ({ visible }) => (
    <span className={"listing-chip listing-chip--visibility " + (visible ? "listing-chip--on" : "listing-chip--off")}>
        <i className={"fa " + (visible ? "fa-eye" : "fa-eye-slash")} aria-hidden="true"></i>
        {visible ? "Visible" : "Hidden"}
    </span>
)

export const formatDateRange = (startIso, endIso) => {
    if (!startIso || !endIso) return "--"
    const options = { month: "short", day: "numeric" }
    const start = new Date(startIso + "T00:00:00")
    const end = new Date(endIso + "T00:00:00")
    return `${start.toLocaleDateString("en-US", options)} – ${end.toLocaleDateString("en-US", options)}, ${end.getFullYear()}`
}
